import ConfirmDialog from "@/components/common/ConfirmDialog";
import UserSearchDialog from "@/components/dialogs/UserSearchDialog";
import PageLayout from "@/components/layout/PageLayout";
import { createUserColumns } from "@/pages/approver/departments/columns.tsx";
import DemsCustomTreeItem from "@/pages/approver/departments/TreeItem.tsx";
import {
  useApproverDepartmentHierarchy,
  useApproverDepartmentUsers,
  useDepartmentUpdate,
  useDepartmentUsersUpdate,
  userDepartmentCreate,
  userDepartmentDelete,
} from "@/services/departmentManageService";
import { DepartmentRequest, DepartmentTreeNode } from "@/types/department";
import { UserDepartmentPositionResponse } from "@/types/user";
import {
  AccountBalance as AccountBalanceIcon,
  Delete as DeleteIcon,
  Inbox as InboxIcon,
  RestartAlt as RestartAltIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import React, { useEffect, useMemo, useRef, useState } from "react";

// id를 string으로 변환하는 유틸
function toStringIdTree(nodes: DepartmentTreeNode[], searchTerm?: string, showOnlyApprovalDept?: boolean): any[] {
  return nodes.map((node) => ({
    ...node,
    id: String(node.id),
    label: searchTerm || showOnlyApprovalDept ? node.fullLabel : node.label,
    children: node.children ? toStringIdTree(node.children, searchTerm) : undefined,
  }));
}

function findDepartmentById(
  nodes: DepartmentTreeNode[],
  id: number | null | undefined
): DepartmentTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findDepartmentById(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

// 부서 정보 변경분만 추출하는 함수
function getModifiedDepartments(local: DepartmentTreeNode[], original: DepartmentTreeNode[]): DepartmentTreeNode[] {
  const result: DepartmentTreeNode[] = [];

  const compare = (localNodes: DepartmentTreeNode[], originalNodes: DepartmentTreeNode[]) => {
    for (const localNode of localNodes) {
      const originalNode = originalNodes.find((n) => n.id === localNode.id);
      if (!originalNode) {
        // 새로 추가된 부서
        result.push(localNode);
      } else {
        // 변경된 부서명, isApproval, isVirtual 등 비교
        if (
          localNode.label !== originalNode.label ||
          localNode.isApproval !== originalNode.isApproval ||
          localNode.isVirtual !== originalNode.isVirtual
        ) {
          result.push(localNode);
        }
        // 자식 노드 재귀 비교
        if (localNode.children && localNode.children.length > 0) {
          compare(localNode.children, originalNode.children || []);
        }
      }
    }
  };

  compare(local, original);
  return result;
}

// DepartmentTreeNode -> DepartmentRequest 변환 함수
function treeNodeToRequest(nodes: DepartmentTreeNode[]): DepartmentRequest[] {
  return nodes.map((node) => ({
    id: node.id < 0 ? null : node.id, // 신규 null
    name: node.label, // label을 name으로 매핑
    isApproval: node.isApproval,
    isVirtual: node.isVirtual,
    children: node.children ? treeNodeToRequest(node.children) : [],
  }));
}

const DepartmentListPage: React.FC = () => {
  const [selectedDeptName, setSelectedDeptName] = useState("");
  const [searchDeptName, setSearchDeptName] = useState("");
  const [selectedDeptId, setSelectedDeptId] = useState<number | null>(null);
  const [localUsers, setLocalUsers] = useState<UserDepartmentPositionResponse[]>([]);
  const [openDeptDialog, setOpenDeptDialog] = useState(false);
  const [openUserSearchDialog, setOpenUserSearchDialog] = useState(false);
  const [showOnlyApprovalDept, setShowOnlyApprovalDept] = useState(false);
  const { data: departmentHierarchy = [] } = useApproverDepartmentHierarchy(searchDeptName, showOnlyApprovalDept);
  const isDeptInServerHierarchy = !!findDepartmentById(departmentHierarchy, selectedDeptId);
  const selectedDeptIdForApi = isDeptInServerHierarchy ? (selectedDeptId ?? -1) : -1;
  const {
    data: departmentUsers = [],
    isLoading: departmentUsersLoading,
    isFetching: departmentUsersFetching,
  } = useApproverDepartmentUsers(selectedDeptIdForApi);
  const [selectedUserIds, setSelectedUserIds] = useState<(number | string)[]>([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
  });

  // departmentHierarchy를 로컬 상태로 복사해서 사용
  const [localDepartmentHierarchy, setLocalDepartmentHierarchy] = useState<DepartmentTreeNode[]>([]);
  const [originalDepartmentHierarchy, setOriginalDepartmentHierarchy] = useState<DepartmentTreeNode[]>([]);
  const [pendingDeleteDeptId, setPendingDeleteDeptId] = useState<number | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  useEffect(() => {
    setLocalDepartmentHierarchy(departmentHierarchy);
    setOriginalDepartmentHierarchy(departmentHierarchy);
  }, [departmentHierarchy]);

  const selectedDept = findDepartmentById(localDepartmentHierarchy, selectedDeptId);
  const selectedDeptLabel = selectedDept?.fullLabel || selectedDept?.label || "";
  const isDeptAddEnabled = selectedDeptId == null || (selectedDept?.isVirtual && selectedDept?.id !== 0);
  // @ts-ignore
  const departmentHierarchyDirty = JSON.stringify(localDepartmentHierarchy) !== JSON.stringify(departmentHierarchy);
  const [newDeptName, setNewDeptName] = useState("");
  const newDeptInputRef = useRef<HTMLInputElement>(null);
  const treeItems = toStringIdTree(localDepartmentHierarchy, searchDeptName, showOnlyApprovalDept);

  useEffect(() => {
    if (selectedDeptId && !departmentUsersLoading) {
      setLocalUsers(departmentUsers);
    }
  }, [departmentUsers, selectedDeptId, departmentUsersLoading]);

  // 뮤테이션
  const departmentUsersUpdateMutation = useDepartmentUsersUpdate();
  const departmentUpdateMutation = useDepartmentUpdate();
  const departmentCreateMutation = userDepartmentCreate();
  const departmentDeleteMutation = userDepartmentDelete();
  // 조직장 체크박스 변경 핸들러
  const handleChangeHeadUsers = (ids: number[], checked: boolean) => {
    setLocalUsers((prev) => prev.map((user) => (ids.includes(user.id) ? { ...user, isHead: checked } : user)));
  };

  const userColumns = useMemo(
    () => createUserColumns(localUsers, handleChangeHeadUsers),
    [localUsers, handleChangeHeadUsers]
  );

  const handleDeptSelectionChange = (_: React.SyntheticEvent | null, ids: string | null) => {
    const selectedDeptId = ids && ids.length ? Number(Array.isArray(ids) ? ids[0] : ids) : null;
    setSelectedDeptId(selectedDeptId);
  };

  const handleDepartmentSearch = () => {
    setSelectedDeptId(null);
    setSearchDeptName(selectedDeptName);
    setLocalUsers([]);
  };

  const handleUserSearchDialogClose = () => {
    setOpenUserSearchDialog(false);
  };

  const handleUserSearchDialogApply = (selectedUsers: UserDepartmentPositionResponse[]) => {
    setLocalUsers((prev) => [...prev, ...selectedUsers.filter((user) => !prev.some((u) => u.userId === user.userId))]);
    setOpenUserSearchDialog(false);
  };

  const isUserDirty = JSON.stringify(localUsers) !== JSON.stringify(departmentUsers);
  const handleResetUsers = () => {
    setLocalUsers(departmentUsers);
    setSelectedUserIds([]);
  };

  const handleDepartmentUsersSave = () => {
    setConfirmDialog({
      open: true,
      title: "부서 사용자 저장",
      content: "부서 사용자 정보를 저장 하시겠습니까?",
      onConfirm: () => {
        departmentUsersUpdateMutation.mutate({
          departmentId: selectedDeptId ?? -1,
          users: localUsers,
        });
      },
    });
  };

  const handleDepartmentUsersDelete = () => {
    setLocalUsers((prev) => prev.filter((user) => !selectedUserIds.includes(user.id)));
    setSelectedUserIds([]);
  };

  // 부서 추가 핸들러
  const handleDeptAdd = React.useCallback(() => {
    if (!newDeptName.trim()) return;

    departmentCreateMutation.mutate({
      parentId: selectedDeptId ?? null,
      request: {
        name: newDeptName,
        isVirtual: true,
      },
    });

    setNewDeptName("");
    setOpenDeptDialog(false);
  }, [newDeptName, selectedDeptId]);

  // 부서 추가 팝업이 열릴 때마다 입력값 초기화 및 포커스
  useEffect(() => {
    if (openDeptDialog) {
      setNewDeptName("");
      setTimeout(() => {
        newDeptInputRef.current?.focus();
      }, 50);
    }
  }, [openDeptDialog]);

  // 부서 삭제 핸들러
  const handleDeptDelete = (deptId?: number) => {
    const targetId = deptId ?? selectedDeptId;
    if (!targetId) return;

    // const targetNode = findDepartmentById(localDepartmentHierarchy, targetId);
    // if (targetNode && targetNode.children && targetNode.children.length > 0) {
    //   alert("하위 부서가 존재합니다. 먼저 하위 부서를 삭제해 주세요.");
    //   return;
    // }

    setConfirmDialog({
      open: true,
      title: "부서 삭제",
      content: "부서(하위 부서 및 소속인원 포함)를 삭제 하시겠습니까?",
      onConfirm: () => {
        departmentDeleteMutation.mutate({
          departmentId: targetId,
        });

        setPendingDeleteDeptId(targetId);
      },
    });
  };

  useEffect(() => {
    if (pendingDeleteDeptId != null) {
      setLocalDepartmentHierarchy((prev) => {
        const deleteDept = (nodes: DepartmentTreeNode[]): DepartmentTreeNode[] =>
          nodes
            .filter((node) => node.id !== pendingDeleteDeptId)
            .map((node) => ({
              ...node,
              children: node.children ? deleteDept(node.children) : [],
            }));
        return deleteDept(prev);
      });
      setPendingDeleteDeptId(null);
      setSelectedDeptId(null);
    }
  }, [pendingDeleteDeptId]);

  // 부서 되돌리기 핸들러
  // @ts-ignore
  const handleResetDepartments = () => {
    setLocalDepartmentHierarchy(originalDepartmentHierarchy);
    setSelectedDeptId(null);
  };

  const handleApprovalDeptClick = () => {
    const targetNode = findDepartmentById(localDepartmentHierarchy, selectedDeptId);
    if (targetNode?.isVirtual) {
      alert("가상 부서는 결재부서로 선택할 수 없습니다.");
      return;
    }

    // if (departmentHierarchyDirty) {
    //   alert("저장되지 않은 부서 변경사항이 있습니다. 저장 후 진행해 주세요.");
    //   return;
    // }

    setConfirmDialog({
      open: true,
      title: "결재부서 설정",
      content: "결재부서로 설정 하시겠습니까?",
      onConfirm: () => {
        if (!selectedDeptId) return;
        departmentUpdateMutation.mutate({
          departmentId: selectedDeptId ?? -1,
          request: { isApproval: true },
        });
      },
    });
  };

  useEffect(() => {
    // 결재부서만 보기 체크 시, 부서명 인풋이 비어 있으면 검색어도 빈 값으로
    if (showOnlyApprovalDept && !selectedDeptName) {
      setSearchDeptName("");
    }
  }, [showOnlyApprovalDept, selectedDeptName]);

  // @ts-ignore
  const handleDepartmentHierarchySave = () => {
    // 변경된 부서만 추출
    const modifiedDepartments = getModifiedDepartments(localDepartmentHierarchy, originalDepartmentHierarchy);
    if (modifiedDepartments.length === 0) {
      return;
    }
    const requestDepartments = treeNodeToRequest(modifiedDepartments);
    // TODO: 실제 API 호출로 변경
    console.log("수정/추가된 부서만 API로 전송:", requestDepartments);
    // 예시: departmentHierarchyUpdateMutation.mutate(requestDepartments);
  };

  // 트리뷰 확장 상태 관리
  const handleExpandedItemsChange = (_: any, nodeIds: string[]) => {
    setExpandedItems(nodeIds);
  };

  const handleShowOnlyApprovalDept = (checked: boolean) => {
    setShowOnlyApprovalDept(checked);
    setSelectedDeptId(null);
    setLocalUsers([]);
  };

  const handleResetSelection = () => {
    setSelectedDeptId(null);
    setLocalUsers([]);
    setSelectedUserIds([]);
  };

  const handleDeptLabelEdit = (id: string, newLabel: string) => {
    departmentUpdateMutation.mutate({
      departmentId: Number(id),
      request: { name: newLabel },
    });
  };

  return (
    <PageLayout title='부서 관리'>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 500, display: "flex", flexDirection: "column", minHeight: 0, gap: 2 }}>
          <Box display='flex' gap={1} alignItems='center'>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                px: 2.8,
                py: 0.5,
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                height: 40,
                mr: 1.2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyApprovalDept}
                    onChange={(_, checked) => handleShowOnlyApprovalDept(checked)}
                    size='small'
                  />
                }
                label='결재부서만 보기'
                sx={{ m: 0 }}
              />
            </Box>
            {/* 기존 부서명 검색 */}
            <TextField
              placeholder='부서명을 입력하세요.(최소 2자이상 20자이하)'
              label='부서 검색'
              size='small'
              inputProps={{ maxLength: 20 }}
              sx={{ flex: 1, minHeight: "40px", boxSizing: "border-box" }}
              value={selectedDeptName}
              onChange={(e) => setSelectedDeptName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleDepartmentSearch();
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {`${selectedDeptName?.length ?? 0}/20 자`}
                      </span>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant='contained'
              sx={{ width: 80, height: 40, bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              onClick={handleDepartmentSearch}
            >
              검색
            </Button>
          </Box>
          {/* 2단: 부서 버튼 */}
          <Box display='flex' flexDirection='row' alignItems='center' width='100%'>
            {/* 왼쪽: 결재부서 버튼 */}
            <Box sx={{ flex: 0.3, display: "flex", justifyContent: "flex-start" }}>
              <Button
                variant='outlined'
                color='primary'
                startIcon={<AccountBalanceIcon />}
                sx={{ height: "40px" }}
                disabled={!selectedDeptId || !isDeptInServerHierarchy}
                onClick={handleApprovalDeptClick}
              >
                결재부서
              </Button>
            </Box>
            {/* 오른쪽: 나머지 버튼들 */}

            {/* <Box sx={{ height: 32, borderLeft: "1.5px solid #ddd", mx: 2 }} /> */}
            <Box sx={{ flex: 1, display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
              <Box sx={{ height: 32, borderLeft: "1.5px solid #ddd", mx: 2 }} />
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                disabled={!isDeptAddEnabled}
                sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, height: "40px" }}
                onClick={() => setOpenDeptDialog(true)}
                onMouseDown={(e) => e.preventDefault()}
              >
                부서 추가
              </Button>
              <Button
                variant='outlined'
                startIcon={<RestartAltIcon />}
                sx={{ height: "40px" }}
                onClick={handleResetSelection}
              >
                선택 해제
              </Button>
              {/* <Box sx={{ height: 32, borderLeft: "1.5px solid #ddd", mx: 2 }} />
              <Button
                variant='contained'
                color='primary'
                startIcon={<SaveIcon />}
                sx={{ width: 85, height: "40px" }}
                disabled={!departmentHierarchyDirty}
                onClick={handleDepartmentHierarchySave}
              >
                저장
              </Button>
              <Button
                variant='contained'
                startIcon={<RestartAltIcon />}
                disabled={JSON.stringify(localDepartmentHierarchy) === JSON.stringify(originalDepartmentHierarchy)}
                onClick={handleResetDepartments}
              >
                되돌리기
              </Button> */}
            </Box>
          </Box>
          {/* 3단: RichTreeView */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <RichTreeView
              key={JSON.stringify(treeItems) + selectedDeptId}
              items={treeItems}
              selectedItems={selectedDeptId != null ? String(selectedDeptId) : null}
              onSelectedItemsChange={handleDeptSelectionChange}
              expandedItems={expandedItems}
              onExpandedItemsChange={handleExpandedItemsChange}
              sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderColor: "#e0e0e0",
                borderRadius: 1,
                p: 1,
              }}
              slots={{
                item: (props) => (
                  <DemsCustomTreeItem
                    {...props}
                    onDelete={(id) => handleDeptDelete(Number(id))}
                    onLabelEdit={handleDeptLabelEdit}
                  />
                ),
              }}
            />
          </Box>
        </Box>
        {/* 우측: 사용자 */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, gap: 2 }}>
          {/* 1단: 부서명 경로 */}
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: 1,
              px: 2,
              minHeight: "40px",
              bgcolor: "#fff",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Typography
              variant='subtitle1'
              sx={{
                width: "100%",
                fontSize: "16px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.5,
                color: "text.secondary",
                textAlign: "right",
              }}
              title={selectedDeptLabel}
            >
              {selectedDeptLabel || "부서를 선택하세요"}
            </Typography>
          </Box>
          {/* 2단: 사용자 버튼 */}
          <Box display='flex' gap={1} justifyContent='flex-end' alignItems='center'>
            <Button
              variant='outlined'
              color='error'
              startIcon={<DeleteIcon />}
              sx={{ height: "40px" }}
              disabled={selectedUserIds.length === 0 || !selectedDeptId}
              onClick={handleDepartmentUsersDelete}
            >
              사용자 삭제
            </Button>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              disabled={!selectedDeptId || !isDeptInServerHierarchy}
              sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" }, height: "40px" }}
              onClick={() => setOpenUserSearchDialog(true)}
            >
              사용자 추가
            </Button>
            <Box sx={{ height: 32, borderLeft: "1.5px solid #ddd", mx: 2 }} />
            <Button
              variant='contained'
              color='primary'
              startIcon={<SaveIcon />}
              sx={{ width: 85, height: "40px" }}
              disabled={!selectedDeptId || !isUserDirty}
              onClick={handleDepartmentUsersSave}
            >
              저장
            </Button>
            <Button
              variant='outlined'
              startIcon={<RestartAltIcon />}
              sx={{ height: "40px" }}
              onClick={handleResetUsers}
              disabled={!isUserDirty}
            >
              되돌리기
            </Button>
          </Box>
          {/* 3단: DataGrid */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <DataGrid
              rows={localUsers}
              columns={userColumns}
              getRowId={(row) => row.id}
              checkboxSelection
              rowHeight={48}
              hideFooter
              disableRowSelectionOnClick
              disableColumnMenu
              loading={departmentUsersFetching}
              onRowSelectionModelChange={(ids) => setSelectedUserIds([...ids])}
              slots={{
                noRowsOverlay: () => (
                  <GridOverlay>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        fontSize: "18px",
                        color: "#888",
                        gap: "8px",
                      }}
                    >
                      <InboxIcon sx={{ fontSize: 40, color: "#888" }} />
                      표시할 데이터가 없습니다.
                    </div>
                  </GridOverlay>
                ),
              }}
              sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                "& .MuiDataGrid-main": { overflow: "hidden" },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiInputBase-root": {
                  boxShadow: "none !important",
                },
                "& .MuiDataGrid-checkboxInput .MuiSvgIcon-root": {
                  fontSize: 20,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* 부서 추가 팝업 */}
      <Dialog
        open={openDeptDialog}
        disableEnforceFocus
        onClose={() => setOpenDeptDialog(false)}
        slotProps={{
          paper: {
            sx: { width: 600, height: 230 },
          },
        }}
      >
        <DialogTitle>부서 추가</DialogTitle>
        <DialogContent>
          <TextField
            label='부서명'
            placeholder='부서명을 입력하세요(최대 50자)'
            value={newDeptName}
            onChange={(e) => setNewDeptName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 50 }}
            inputRef={newDeptInputRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newDeptName.trim()) {
                handleDeptAdd();
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='end'>
                    <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                      {`${newDeptName?.length ?? 0}/50 자`}
                    </span>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              mt: 1,
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            pt: 1,
            pb: 3,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Button variant='outlined' onClick={() => setOpenDeptDialog(false)}>
            취소
          </Button>
          <Button
            variant='contained'
            onClick={(e) => {
              e.preventDefault();
              handleDeptAdd();
            }}
            sx={{
              opacity: !newDeptName.trim() ? 0.5 : 1,
              pointerEvents: !newDeptName.trim() ? "none" : "auto",
            }}
          >
            등록
          </Button>
        </DialogActions>
      </Dialog>

      {/* 사용자 검색 다이얼로그 */}
      <UserSearchDialog
        open={openUserSearchDialog}
        onClose={handleUserSearchDialogClose}
        onApply={handleUserSearchDialogApply}
        localUsers={localUsers}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
      />
    </PageLayout>
  );
};

export default DepartmentListPage;
